import React from "react";
import api from "../../lib/api";
import { Plus, Download, Users, Trash2, X, Copy, Pencil } from "lucide-react";

function formatDateFR(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUT_BADGE = {
  ouverte: { label: "Ouverte", cls: "bg-accent-deep/15 text-accent-light border-accent-deep/40" },
  complete: { label: "Complète", cls: "bg-text-cream/10 text-text-cream/60 border-white/15" },
  fermee: { label: "Fermée", cls: "bg-white/5 text-text-cream/40 border-white/10" },
  annulee: { label: "Annulée", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export default function AdminSessions() {
  const [sessions, setSessions] = React.useState([]);
  const [workshops, setWorkshops] = React.useState([]);
  const [participants, setParticipants] = React.useState(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const load = () => api.get("/admin/sessions").then(r => setSessions(r.data));
  React.useEffect(() => {
    load();
    api.get("/workshops").then(r => setWorkshops(r.data));
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette session ? Les réservations existantes resteront en base.")) return;
    await api.delete(`/admin/sessions/${id}`);
    load();
  };

  const duplicate = async (id) => {
    if (!window.confirm("Dupliquer cette session ? Une copie sera créée avec la date +1 mois.")) return;
    try {
      await api.post(`/admin/sessions/${id}/duplicate`);
      load();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const showParticipants = async (s) => {
    const r = await api.get(`/admin/sessions/${s.id}/participants`);
    setParticipants({ session: s, list: r.data });
  };

  const downloadCSV = async (id) => {
    const token = localStorage.getItem("se_admin_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sessions/${id}/participants.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `participants-${id}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-sessions">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Workshop</div>
          <h1 className="h-display text-4xl md:text-5xl">Sessions.</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-session-btn">
          <Plus size={14}/> Nouvelle session
        </button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-text-cream/50 eyebrow text-[0.65rem]">
            <tr>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Horaire</th>
              <th className="text-left p-4">Prix</th>
              <th className="text-left p-4">Places</th>
              <th className="text-left p-4">Revenu CHF</th>
              <th className="text-left p-4">Statut</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => {
              const sold = s.places_max - s.places_restantes;
              const badge = STATUT_BADGE[s.statut] || STATUT_BADGE.ouverte;
              return (
                <tr key={s.id} className="border-t border-white/5 font-light" data-testid={`row-${s.id}`}>
                  <td className="p-4 font-serif italic text-accent-light">{formatDateFR(s.date)}</td>
                  <td className="p-4 text-text-cream/70">{s.heure_debut}–{s.heure_fin}</td>
                  <td className="p-4">CHF {s.prix_actuel}</td>
                  <td className="p-4">
                    <span className="font-serif text-base">{sold}<span className="text-text-cream/40">/{s.places_max}</span></span>
                    {s.places_restantes <= 2 && s.places_restantes > 0 && (
                      <div className="text-[0.65rem] text-accent-light uppercase tracking-widest mt-0.5">
                        {s.places_restantes === 1 ? "Dernière" : `+${s.places_restantes}`}
                      </div>
                    )}
                  </td>
                  <td className="p-4">{s.revenue_chf}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.2em] border ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => setEditing(s)} className="text-text-cream/60 hover:text-accent-light" title="Modifier" data-testid={`edit-${s.id}`}>
                        <Pencil size={16} strokeWidth={1.5}/>
                      </button>
                      <button onClick={() => duplicate(s.id)} className="text-text-cream/60 hover:text-accent-light" title="Dupliquer" data-testid={`duplicate-${s.id}`}>
                        <Copy size={16} strokeWidth={1.5}/>
                      </button>
                      <button onClick={() => showParticipants(s)} className="text-text-cream/60 hover:text-accent-light" title="Participants" data-testid={`participants-${s.id}`}>
                        <Users size={16} strokeWidth={1.5}/>
                      </button>
                      <button onClick={() => downloadCSV(s.id)} className="text-text-cream/60 hover:text-accent-light" title="Export CSV" data-testid={`csv-${s.id}`}>
                        <Download size={16} strokeWidth={1.5}/>
                      </button>
                      <button onClick={() => remove(s.id)} className="text-text-cream/60 hover:text-red-400" title="Supprimer" data-testid={`delete-${s.id}`}>
                        <Trash2 size={16} strokeWidth={1.5}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {sessions.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-text-cream/40 italic">Aucune session</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateSessionModal workshops={workshops} onClose={() => { setShowCreate(false); load(); }} />}
      {editing && <EditSessionModal session={editing} onClose={() => { setEditing(null); load(); }} />}
      {participants && <ParticipantsModal data={participants} onClose={() => setParticipants(null)} />}
    </div>
  );
}

function CreateSessionModal({ workshops, onClose }) {
  const [form, setForm] = React.useState({
    workshop_id: workshops[0]?.id || "",
    date: "",
    heure_debut: "14:00",
    heure_fin: "18:00",
    prix_actuel: 240,
    places_max: 6,
    lieu: "Studio Euclides, Route de Crochy 20, 1024 Écublens",
  });
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/admin/sessions", form);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur de création");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="bg-dark border border-white/15 p-8 w-full max-w-lg" data-testid="create-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl">Nouvelle session</h3>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <label className="eyebrow mb-2 block">Workshop</label>
            <select required value={form.workshop_id} onChange={(e)=>setForm({...form, workshop_id:e.target.value})} className="input-editorial bg-dark" data-testid="new-workshop">
              {workshops.map(w => <option key={w.id} value={w.id}>{w.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Date</label>
            <input type="date" required value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} className="input-editorial" data-testid="new-date"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Prix CHF</label>
            <input type="number" required value={form.prix_actuel} onChange={(e)=>setForm({...form, prix_actuel:Number(e.target.value)})} className="input-editorial" data-testid="new-prix"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Heure début</label>
            <input type="time" required value={form.heure_debut} onChange={(e)=>setForm({...form, heure_debut:e.target.value})} className="input-editorial"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Heure fin</label>
            <input type="time" required value={form.heure_fin} onChange={(e)=>setForm({...form, heure_fin:e.target.value})} className="input-editorial"/>
          </div>
          <div className="col-span-2">
            <label className="eyebrow mb-2 block">Capacité (1–20) · défaut 6</label>
            <input type="number" min={1} max={20} required value={form.places_max} onChange={(e)=>setForm({...form, places_max:Number(e.target.value)})} className="input-editorial" data-testid="new-capacity"/>
            <p className="text-text-cream/40 text-xs mt-1 font-light">Capacité par défaut : 6 participants. Modifiable de 1 (masterclass) à 20 (event).</p>
          </div>
          <div className="col-span-2">
            <label className="eyebrow mb-2 block">Lieu</label>
            <input value={form.lieu} onChange={(e)=>setForm({...form, lieu:e.target.value})} className="input-editorial"/>
          </div>
        </div>
        {err && <div className="text-red-400 text-sm mt-4" data-testid="create-error">{err}</div>}
        <button type="submit" disabled={loading} className="btn-primary mt-8 w-full justify-center" data-testid="new-submit">
          {loading ? "Création…" : "Créer la session"}
        </button>
      </form>
    </div>
  );
}

function EditSessionModal({ session, onClose }) {
  const sold = session.places_max - session.places_restantes;
  const [form, setForm] = React.useState({
    date: session.date,
    heure_debut: session.heure_debut,
    heure_fin: session.heure_fin,
    prix_actuel: session.prix_actuel,
    places_max: session.places_max,
    statut: session.statut,
    lieu: session.lieu,
  });
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.patch(`/admin/sessions/${session.id}`, form);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="bg-dark border border-white/15 p-8 w-full max-w-lg" data-testid="edit-modal">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="font-serif text-2xl">Modifier la session</h3>
            <div className="eyebrow text-text-cream/40 mt-1">{sold} inscrit{sold>1?"s":""} sur {session.places_max}</div>
          </div>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="eyebrow mb-2 block">Date</label>
            <input type="date" required value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} className="input-editorial"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Prix CHF</label>
            <input type="number" required value={form.prix_actuel} onChange={(e)=>setForm({...form, prix_actuel:Number(e.target.value)})} className="input-editorial" data-testid="edit-prix"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Heure début</label>
            <input type="time" required value={form.heure_debut} onChange={(e)=>setForm({...form, heure_debut:e.target.value})} className="input-editorial"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Heure fin</label>
            <input type="time" required value={form.heure_fin} onChange={(e)=>setForm({...form, heure_fin:e.target.value})} className="input-editorial"/>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Capacité (1–20)</label>
            <input type="number" min={1} max={20} required value={form.places_max} onChange={(e)=>setForm({...form, places_max:Number(e.target.value)})} className="input-editorial" data-testid="edit-capacity"/>
            <p className="text-text-cream/40 text-xs mt-1 font-light">Min : {sold} (déjà inscrits)</p>
          </div>
          <div>
            <label className="eyebrow mb-2 block">Statut</label>
            <select value={form.statut} onChange={(e)=>setForm({...form, statut:e.target.value})} className="input-editorial bg-dark" data-testid="edit-statut">
              <option value="ouverte">Ouverte</option>
              <option value="complete">Complète</option>
              <option value="fermee">Fermée</option>
              <option value="annulee">Annulée</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="eyebrow mb-2 block">Lieu</label>
            <input value={form.lieu} onChange={(e)=>setForm({...form, lieu:e.target.value})} className="input-editorial"/>
          </div>
        </div>
        {err && <div className="text-red-400 text-sm mt-4 border-l-2 border-red-400 pl-3 py-2 bg-red-500/5" data-testid="edit-error">{err}</div>}
        <button type="submit" disabled={loading} className="btn-primary mt-8 w-full justify-center" data-testid="edit-submit">
          {loading ? "Enregistrement…" : "Enregistrer les modifications"}
        </button>
      </form>
    </div>
  );
}

function ParticipantsModal({ data, onClose }) {
  const { session, list } = data;
  const togglePresent = async (id, present) => {
    await api.patch(`/admin/reservations/${id}/present?present=${present}`);
    onClose();
  };
  const sendReminder = async (r) => {
    if (!window.confirm(`Envoyer le rappel J-1 à ${r.prenom} ${r.nom} (${r.email}) ?`)) return;
    try {
      await api.post(`/admin/reservations/${r.id}/send-reminder`);
      alert(`Rappel envoyé à ${r.email}`);
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };
  const resendConfirmation = async (r) => {
    if (!window.confirm(`Renvoyer la confirmation à ${r.email} ?`)) return;
    try {
      await api.post(`/admin/reservations/${r.id}/send-confirmation`);
      alert(`Confirmation renvoyée à ${r.email}`);
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-dark border border-white/15 p-8 w-full max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="participants-modal">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="font-serif text-3xl italic">{formatDateFR(session.date)}</h3>
            <div className="eyebrow text-text-cream/50 mt-1">{list.length} inscription{list.length>1?"s":""}</div>
          </div>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-text-cream/50 eyebrow text-[0.65rem]">
              <tr>
                <th className="text-left p-3">Nom</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Paiement</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-left p-3">Présence</th>
                <th className="text-left p-3">Rappel J-1</th>
                <th className="text-left p-3">Actions emails</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id} className="border-t border-white/5 font-light">
                  <td className="p-3 font-serif">{r.prenom} {r.nom}</td>
                  <td className="p-3 text-text-cream/70 break-all">{r.email}</td>
                  <td className="p-3">{r.type_paiement === "integral" ? "Intégral" : "Acompte"} · CHF {r.montant_paye}</td>
                  <td className="p-3 capitalize">{r.statut}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button onClick={() => togglePresent(r.id, true)} className={`px-2 py-1 text-xs border ${r.present === true ? "bg-accent-deep text-dark border-accent-deep" : "border-white/15 hover:border-accent-deep"}`}>Présent</button>
                      <button onClick={() => togglePresent(r.id, false)} className={`px-2 py-1 text-xs border ${r.present === false ? "bg-red-500/30 border-red-500/50" : "border-white/15 hover:border-red-400/60"}`}>Absent</button>
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {r.reminder_sent ? <span className="text-accent-light">✓ Envoyé</span> : <span className="text-text-cream/40">—</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {r.statut === "paye" && (
                        <button onClick={() => sendReminder(r)} className="px-2 py-1 text-[0.65rem] uppercase tracking-widest border border-white/15 hover:border-accent-deep hover:text-accent-light">
                          Rappel J-1
                        </button>
                      )}
                      <button onClick={() => resendConfirmation(r)} className="px-2 py-1 text-[0.65rem] uppercase tracking-widest border border-white/15 hover:border-accent-deep hover:text-accent-light">
                        Confirmation
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={7} className="p-8 text-center italic text-text-cream/40">Aucun participant inscrit pour le moment.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
